import { useEffect, useMemo, useRef } from "react";
import {
  TAU, cardTransform, depthFilter, depthOf, helixWorld, helixY, lerp, strandPhase,
} from "./helixMath.js";
import {
  collapseAt, liftAt, pourStretchFor, radiusScaleAt, sequenceClock, speedAt,
} from "./sequence.js";
import FileCard from "./FileCard.jsx";
import "./photoHelix.css";

// A double helix of a claim's files, turning slowly, cards leaving the bottom and
// arriving back at the top. Self-contained: see README.md in this folder.
//
// WHY A rAF LOOP AND NOT CSS KEYFRAMES.
// The pure-CSS version of this is real — one keyframe per card with a negative
// animation-delay to stagger them — and it was the first thing I tried. It is
// rejected because of where this is going rather than what it does today: the
// point is that the files gather INTO the cylinder and then pour OUT of it into a
// folder, and both handoffs need the exact position of a specific card at a
// specific instant. CSS will not tell you that.
//
// React renders the cards ONCE. Per-frame writes go straight to the DOM nodes
// through refs — putting forty transforms through state would re-render the tree
// sixty times a second to change nothing but a string.

const DEFAULTS = {
  strands: 2,
  perStrand: 20,      // ceiling on slots per strand; the actual count follows the files
  minRadiusScale: 0.62, // a handful of cards pull the cylinder in rather than padding it out
  // Idle rotation in REVOLUTIONS per second, used only when no sequence is
  // running — the lab sitting there. Once a run starts, speedAt() owns the
  // speed. It read "slots per second" until 2026-08-02, left over from when the
  // cards descended; nothing descends now, so a slot is not a unit any more.
  speed: 0.25,
  // Master multiplier on the strand height; the count-driven ramp does the real
  // work (see spreadScale). It was a flat 1.5 when the cards SCROLLED, which put
  // a single file off-screen entirely once they stopped. A sparse ring now sits
  // well inside the frame, while a full cylinder deliberately runs past the top
  // and bottom — with forty cards that is a column extending beyond the view,
  // which is the tube reading, and the vignette fades the ends out.
  spanFactor: 0.74,
  radius: 560,
  perspective: 1400,
  cardWidth: 260,
  cardHeight: 180,
  theme: "light",     // "light" = fade into haze · "dark" = fade into shadow
  backdrop: null,     // any CSS colour; null keeps the theme's own
  depthFloor: 0.14,   // how far gone the far side of the cylinder is
  maxBlur: 3.5,       // blur compounds with fading; 5 turned the far half to mush
};

// What identifies a FILE, as opposed to a card. The two are not the same: slots
// are rounded up to fill both strands, so 13 files ride 14 cards and one of them
// appears twice. Counting cards would tell the user they archived 14 files.
const fileKeyOf = (item) => item?.key ?? item?.src ?? item?.ext ?? "";

// "#rrggbb" -> "r, g, b", so a chosen backdrop can also be used at partial alpha
// while it fades in. Anything not a plain hex is passed through and simply will
// not fade — a named colour still works, it just arrives at once.
function toRgb(colour) {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(String(colour).trim());
  return m ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}` : null;
}

export default function PhotoHelix({
  items = [], paused = false, runningSince = null, onLanded, origins = null,
  // Where the pour ends up, as an offset from the middle of the frame. Defaults
  // to the middle; the Files tab aims it at the button that was actually pressed.
  target = null,
  // A REF holding when the work being covered finished (a performance.now()
  // stamp), or null while it is still going. A ref rather than a prop because the
  // answer changes mid-run: as a prop it would land in the effect's deps, restart
  // the render loop and reset the count just as the archive arrived. Absent =
  // nothing to wait for, which is what the lab passes.
  releasedAtRef = null,
  className = "", style, ...options
}) {
  const cfg = { ...DEFAULTS, ...options };
  const sceneRef = useRef(null);
  const cardRefs = useRef([]);
  const spanRef = useRef(0);
  // Distance travelled down the strand, accumulated per frame.
  //
  // NOT elapsed × speed. Speed changes during the sequence, and multiplying the
  // whole elapsed time by the CURRENT speed retroactively rewrites where every
  // card has already been — they jump. Integrating speed over time is the only
  // way a card's position stays a continuous function of what it has done.
  const travelRef = useRef(0);
  const lastRef = useRef(0);
  // Read out here so the render loop depends on two numbers rather than on an
  // object literal, which would be a new reference on every parent render and
  // restart the animation.
  const tx = target?.x ?? 0;
  const ty = target?.y ?? 0;

  // How many slots each strand gets, from how many files there actually are.
  //
  // ONE CARD PER FILE, NEVER MORE. There used to be a floor of five per strand so
  // a three-file claim still read as a spiral, and it did — but picking four
  // photos then put TEN cards in the air (Robert 2026-08-01). Showing fewer than
  // reality is fine: at 571 files the cylinder is visibly a sample and nobody is
  // counting. Showing MORE is a different thing entirely. At four files you can
  // count, the same photo goes past your eye three times, and the folder
  // underneath says "4 files" while the animation appears to show ten. Of the two
  // channels the number is the one that has to be believed, so the pictures have
  // to agree with it.
  //
  // The cap stays: 571 files would otherwise put 571 textures on the compositor
  // for a cylinder that can only show forty.
  const perStrand = useMemo(() => {
    if (items.length === 0) return 0;
    return Math.max(1, Math.min(cfg.perStrand, Math.ceil(items.length / cfg.strands)));
  }, [items.length, cfg.strands, cfg.perStrand]);

  // Few cards, smaller cylinder. This is what the padding was really for — four
  // cards on a 560px radius are four lonely dots rather than a spiral — but the
  // honest way to get it is to bring the cylinder in, not to invent files.
  const radius = useMemo(() => {
    const count = perStrand * cfg.strands;
    const scale = Math.min(1, cfg.minRadiusScale + (1 - cfg.minRadiusScale) * (count / 12));
    return cfg.radius * scale;
  }, [perStrand, cfg.strands, cfg.radius, cfg.minRadiusScale]);


  // Files are dealt round-robin across the strands rather than in blocks, so a
  // claim whose photos arrived in order does not end up with all the befores on
  // one strand and all the afters on the other.
  //
  // SURPLUS SLOTS ARE LEFT EMPTY rather than filled with a repeat. Both strands
  // have to hold the same number of slots or they descend at different rates, so
  // the slot grid rounds UP — which meant a single file was dealt to strand one
  // and then again to strand two, and one photo appeared as two (Robert
  // 2026-08-01, who reasonably read it as a mirror). It is not a mirror; it is the
  // second strand of a double helix having nothing of its own to carry. An unused
  // slot simply renders no card.
  //
  // Above capacity the sample is spread ACROSS the claim rather than taken from
  // the front: 571 files into 40 slots should look like the whole job, not like
  // whichever forty were uploaded first.
  const cards = useMemo(() => {
    const out = [];
    if (items.length === 0) return out;
    const slots = perStrand * cfg.strands;

    // Each strand is laid out over ITS OWN card count, as an even fraction.
    //
    // This replaces `index / (perStrand - 1)` fed through `turns`, which was even
    // in height and badly uneven in ANGLE: the step between cards came out as
    // turns/(perStrand-1) of a revolution, so five cards over two turns landed on
    // two angles and piled on top of each other. Nine and ten files were the worst
    // of it (Robert 2026-08-01). Spacing by fraction-of-strand is even for any
    // count by construction — there is no arithmetic left to go wrong — and the
    // two strands still sit half a turn apart, so it is still a double helix.
    const filled = [];
    for (let strand = 0; strand < cfg.strands; strand++) {
      const inStrand = [];
      for (let index = 0; index < perStrand; index++) {
        const ordinal = index * cfg.strands + strand;
        if (ordinal >= items.length) continue; // an empty slot, not a repeated file
        inStrand.push({ ordinal, item: items.length <= slots
          ? items[ordinal]
          : items[Math.floor((ordinal * items.length) / slots)] });
      }
      filled.push(inStrand);
    }

    for (let strand = 0; strand < filled.length; strand++) {
      const inStrand = filled[strand];
      for (let i = 0; i < inStrand.length; i++) {
        const { ordinal, item } = inStrand[i];
        out.push({
          key: `${strand}-${i}`,
          // Where this card sits along its own strand, 0..1. Drives both the
          // angle (evenly round) and the height (evenly down).
          seat: inStrand.length <= 1 ? 0 : i / inStrand.length,
          rise: inStrand.length <= 1 ? 0.5 : i / (inStrand.length - 1),
          phase: strandPhase(strand, cfg.strands),
          ordinal,
          item,
          fileKey: fileKeyOf(item),
        });
      }
    }
    return out;
  }, [items, cfg.strands, perStrand]);

  // HOW CROWDED THE CYLINDER IS, 0 (a card or two) to 1 (a proper tube). Three
  // things depend on it, and all three are the same problem seen from different
  // sides: a cylinder is only a cylinder when there is a crowd on it.
  //
  //   • a card on the wall of a cylinder turns EDGE-ON twice per revolution, where
  //     it has no width and disappears. With eighty cards someone is always facing
  //     you and it reads as a tube; with one, the only card blinks out twice a
  //     turn (Robert 2026-08-01: "it blurs and flickers");
  //   • the far side of a crowd is background, so it should fade and blur away.
  //     The far side of a crowd of two is half the content;
  //   • mirroring on the back only needs hiding when there is a back.
  // TWO SHAPES, AND A RAMP BETWEEN THEM (Robert 2026-08-01, after watching 9, 44
  // and 571 side by side).
  //
  //   under 12 cards — a RING. Few enough to count, so they face the camera,
  //     sit on one turn well apart, and the cylinder pulls in tight. This is the
  //     one he called perfect at nine files.
  //   12 and up — a HELIX. The thing the original got right was that it SPREAD
  //     the photos out; what was wrong with it was the descending, and that is
  //     already gone. So the spiral comes back as pure geometry: more turns and a
  //     taller strand as the count grows.
  //
  // Without the ramp, forty cards inherited the ring's one turn and half-frame
  // height — 18° and 27px apart, on cards 180px tall — and simply piled up.
  const SPARSE = 12;
  const FULL = 40; // the cap: 20 slots x 2 strands
  const helixT = Math.min(1, Math.max(0, (cards.length - SPARSE) / (FULL - SPARSE)));
  const turns = 1 + helixT;            // one turn when sparse, two when full
  const spreadScale = 0.5 + 1.0 * helixT; // half the frame, up to one and a half
  const crowd = Math.min(1, cards.length / 16);
  const billboard = cards.length < SPARSE;

  // The strand is taller than its container so cards enter and leave past the
  // edges. Measured rather than assumed, so this works at any size.
  useEffect(() => {
    const el = sceneRef.current;
    if (!el) return undefined;
    const measure = () => { spanRef.current = el.clientHeight * cfg.spanFactor; };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cfg.spanFactor]);

  useEffect(() => {
    if (cards.length === 0) return undefined;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    lastRef.current = performance.now();

    // Counting files into the folder.
    //
    // Two different numbers, and the whole trap is telling them apart. The
    // cylinder shows at most 40 cards; a claim can hold 571 files. So the cards
    // are a SAMPLE, and the count has to report the archive rather than the
    // sample — job 75739 finished on "40 / 571" while the animation had plainly
    // just swallowed the lot (Robert 2026-08-01).
    //
    // So: measure how much of the cylinder has poured, and scale that to the real
    // total. When files fit on the cylinder the two are the same set and the
    // count is exact; when they do not, it climbs in steps to the true figure and
    // lands on it precisely, because a full cylinder means a full archive.
    const landed = new Set();
    const onCylinder = new Set(cards.map((c) => c.fileKey)).size;
    let reported = -1;

    const draw = (now) => {
      const dt = Math.min((now - lastRef.current) / 1000, 0.1); // clamped: a
      lastRef.current = now;                                    // backgrounded tab
      // Few cards, shorter strand. Spreading four files over the full height
      // leaves them scattered and lonely; the group should read as a group.
      const spread = spanRef.current * spreadScale;

      // Sequence time, or null when idling. `runningSince` is a timestamp so the
      // parent starts the run without this component re-rendering.
      let seq = null;
      if (runningSince != null) {
        const raw = (now - runningSince) / 1000;
        const stamp = releasedAtRef ? releasedAtRef.current : 0;
        const released = stamp == null ? Infinity : Math.max(0, (stamp - runningSince) / 1000);
        seq = sequenceClock(raw, released, pourStretchFor(items.length));
      }
      // REDUCED MOTION only stops the SPIN, which is honest about what it is: the
      // ambient turn is the part that runs forever, so the lab sitting idle goes
      // quiet. It does NOT stop the sequence — the burst, the collapse and the
      // pour all still play at full speed, because they are the feature and there
      // is nothing left if they go. Anyone wanting to honour the preference
      // properly should skip the overlay entirely and download the zip, rather
      // than freeze a half-formed cylinder on screen.
      const still = reduced.matches || paused;
      if (!still) travelRef.current += (seq == null ? cfg.speed : speedAt(seq)) * dt;

      for (let i = 0; i < cards.length; i++) {
        const el = cardRefs.current[i];
        if (!el) continue;
        const card = cards[i];
        // A card's HEIGHT is fixed — its place on the strand, and it stays there.
        // Only the angle advances, so the whole cylinder turns as one body rather
        // than scrolling past. `travel` is now a rotation, not a descent.
        //
        // Spread over (perStrand - 1) so the ends land ON 0 and 1 and the set is
        // CENTRED. Dividing by perStrand pushes everything toward the top — with
        // two slots the cards sat at 0 and 0.5, i.e. the top edge and the middle,
        // with nothing below the centre line at all.
        // Position comes straight off the card's seat on its strand — even by
        // construction, whatever the file count. The arithmetic that used to work
        // it out from a turn count is gone; see the note where `seat` is built.
        const home = card.rise;
        // `turns` multiplies the seat, so a crowded strand wraps further round and
        // adjacent cards stay apart. Two cards CAN land on the same angle at two
        // turns — that is what a two-turn helix is, and they are far apart
        // vertically, so they do not overlap.
        const angle = card.seat * turns * TAU + card.phase + travelRef.current * TAU;
        // Where the card FACES, which is not the same as where it sits. On a
        // crowded cylinder it lies on the wall and turns with it. With only a few
        // it keeps facing the camera and merely orbits — so it never passes
        // edge-on, never blinks out, and never shows its mirrored back.
        const facing = billboard ? 0 : angle;

        // The pour: the card is drawn from its place on the cylinder toward the
        // target while it keeps turning, so it spirals in rather than sliding
        // along a straight line. Blending the POSITION rather than shrinking the
        // radius is what lets the destination be somewhere other than the axis —
        // the Files tab sends them to the button that was pressed.
        // Bottom of the cylinder pours first. Keyed on `home`, which cannot move
        // — when this was keyed on a descending position, a card that ran off the
        // bottom wrapped to the top, its delay jumped with it, and one already
        // halfway into the folder was thrown back to be sucked in a second time.
        const c = seq == null ? 0 : collapseAt(seq, home);
        const rScale = seq == null ? 1 : radiusScaleAt(seq);
        const slot = helixWorld(angle, helixY(home, spread), radius * rScale);
        let pos = {
          x: lerp(slot.x, tx, c),
          y: lerp(slot.y, ty, c),
          z: lerp(slot.z, 0, c),
          angle: facing,
          scale: 1 - 0.88 * c,
        };

        // The lift: from the card's own thumbnail on the Files tab to its slot on
        // the cylinder. Both are just positions now, so this is a blend rather
        // than a special case — the reason helixWorld exists.
        const origin = origins?.get(card.fileKey);
        if (origin && seq != null) {
          const l = liftAt(seq, i, cards.length);
          if (l < 1) {
            pos = {
              x: lerp(origin.x, pos.x, l),
              y: lerp(origin.y, pos.y, l),
              z: lerp(0, pos.z, l),
              angle: pos.angle * l,       // flat on the page, turned once it arrives
              scale: lerp(origin.scale, pos.scale, l),
            };
          }
        }

        el.style.transform = cardTransform(pos);
        // Depth cueing has to fade IN with the lift, or a photo sitting on the
        // page inherits the far side's blur and darkness while it is still
        // supposed to be indistinguishable from the real thumbnail underneath.
        // A sparse cylinder barely fades or blurs: with two cards the "far side"
        // is half of everything you have, and washing it out leaves almost
        // nothing on screen.
        const floor = lerp(0.72, cfg.depthFloor, crowd);
        const blur = cfg.maxBlur * crowd;
        const lifted = origin && seq != null ? liftAt(seq, i, cards.length) : 1;
        el.style.filter = lifted < 1
          ? depthFilter(lerp(1, depthOf(angle), lifted), {
            mode: cfg.theme, floor: lerp(1, floor, lifted), maxBlur: blur * lifted,
          })
          : depthFilter(depthOf(angle), { mode: cfg.theme, floor, maxBlur: blur });
        // Gone only at the very end, so cards disappear INTO the folder rather
        // than fading out on their way to it.
        el.style.opacity = c > 0.75 ? String(1 - (c - 0.75) / 0.25) : "1";

        // Counted at 0.98 rather than 1: the last sliver of the ease is
        // imperceptible movement, and a number that ticks over the instant the
        // card visually disappears beats one that waits for the maths to finish.
        if (c >= 0.98) landed.add(fileKeyOf(card.item));
      }

      // Only when it changes — a few dozen renders across the whole sequence,
      // not sixty a second.
      const shown = onCylinder === 0 ? 0 : Math.round((items.length * landed.size) / onCylinder);
      if (shown !== reported) {
        reported = shown;
        onLanded?.(shown);
      }
      frame = requestAnimationFrame(draw);
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [
    cards, perStrand, radius, crowd, billboard, turns, spreadScale,
    paused, runningSince, onLanded, origins, releasedAtRef, items.length,
    cfg.speed, cfg.theme, cfg.depthFloor, cfg.maxBlur, tx, ty,
  ]);

  return (
    <div
      ref={sceneRef}
      className={`ph-scene ${className}`}
      style={{
        ...(cfg.backdrop ? { "--ph-backdrop": cfg.backdrop, "--ph-backdrop-rgb": toRgb(cfg.backdrop) } : {}),
        "--ph-perspective": `${cfg.perspective}px`,
        "--ph-card-width": `${cfg.cardWidth}px`,
        "--ph-card-height": `${cfg.cardHeight}px`,
        ...style,
      }}
    >
      <div className="ph-world">
        {cards.map((card, i) => (
          <FileCard
            key={card.key}
            item={card.item}
            innerRef={(el) => { cardRefs.current[i] = el; }}
          />
        ))}
      </div>
      <div className="ph-vignette" />
    </div>
  );
}
